import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import can from 'ember-can/helpers/can';
import RouteTemplate from 'ember-route-template';
import { gt } from 'ember-truth-helpers';
import GroupsTable from 'garaje/components/location-overview/locations/groups-table';
import LocationsTableWithGroups from 'garaje/components/location-overview/locations/locations-table';
import LocationsMap from 'garaje/components/location-overview/locations/map';
import LocationsTable from 'garaje/components/location-overview/locations-table';
import UiHeader from 'garaje/components/ui/header';
import UiPage from 'garaje/components/ui/page';
import PageSection from 'garaje/components/ui/page/section';
import UiPill from 'garaje/components/ui-pill';
import type AverageMonthlyVisitorCount from 'garaje/models/average-monthly-visitor-count';
import type GroupModel from 'garaje/models/group';
import type LocationsService from 'garaje/services/locations';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import { filterBy } from 'macro-decorators';

import type { LocationOverviewLocationsRouteModel } from './route';

interface LocationOverviewLocationsSignature {
  Args: { model: LocationOverviewLocationsRouteModel };
}

class LocationOverviewLocationsTemplate extends Component<LocationOverviewLocationsSignature> {
  @service declare skinnyLocations: SkinnyLocationsService;
  @service declare locations: LocationsService;

  @filterBy('args.model.groups', 'isNew', false) declare groups: GroupModel[];

  get averageMonthlyVisitorCount(): AverageMonthlyVisitorCount[] {
    if ('groups' in this.args.model) return [];
    return this.args.model.slice();
  }

  <template>
    <UiPage class="min-w-[900px]">
      {{#if (can "manage locations-grouping")}}
        <UiHeader>Locations</UiHeader>

        {{! Groups }}
        <PageSection>
          <div class="flex items-center">
            <div class="flex-auto">
              <h3>
                Groups
                {{#if (gt this.groups.length 0)}}
                  <span data-test-total-groups class="text-carbon-50 opacity-75">{{this.groups.length}}</span>
                {{else}}
                  <UiPill @fill="blue">New</UiPill>
                {{/if}}
              </h3>
              <p class="mt-2 mb-0 text-sm text-carbon-50">Use groups to organize your locations</p>
            </div>
            <LinkTo data-test-new-group @route="location-overview.locations.groups.new" class="btn btn--brand">
              Create group
            </LinkTo>
          </div>

          <GroupsTable data-test-groups-table @groups={{this.groups}} />
        </PageSection>

        {{! Locations }}
        <PageSection>
          <div class="flex items-center">
            <div class="flex-auto">
              <h3>Locations
                <span
                  data-test-total-locations
                  class="text-carbon-50 opacity-75"
                >{{this.skinnyLocations.content.length}}</span></h3>
              <p class="mt-2 mb-0 text-sm text-carbon-50">All locations in your company</p>
            </div>
            {{#if (can "create locations")}}
              <LinkTo data-test-new-location @route="new-location" class="btn btn--brand">
                Create location
              </LinkTo>
            {{/if}}
          </div>

          <LocationsMap data-test-locations-map @locations={{this.skinnyLocations.content}} />

          <LocationsTableWithGroups
            data-test-groups-locations-table
            @locations={{this.skinnyLocations.content}}
            @groups={{this.groups}}
          />
        </PageSection>
      {{else}}
        <LocationsTable
          @locations={{this.locations.readableByCurrentAdmin}}
          @averageMonthlyVisitorCount={{this.averageMonthlyVisitorCount}}
        />
      {{/if}}

      {{outlet}}
    </UiPage>
  </template>
}

export default RouteTemplate(LocationOverviewLocationsTemplate);
